import { StatTable } from "components/StatsTable/StatTable";
import {
  anyStringContains,
  arrayCompareWithShallowEquals,
  nonUndefined,
} from "Utility";
import {
  SharedTeamTrackStats,
  getSharedTeamTrackStats,
} from "stats/Track/Team/SharedTeamTrackStats";
import { StatTableColumn } from "components/StatsTable/StatTableColumn";
import { Match, matchContainsRoster } from "model/Match/Match";
import { sharedTrackTeamStatTableColumns } from "components/StatsTable/ColumnConfigs/Track/Team/Shared";
import { selectRosterDisplayName, selectRosterOrgId } from "selectors/Roster";
import {
  getMatchesScopedRosterIds,
  ScopedRosterId,
} from "model/Roster/ScopedRosterId";
import {
  selectCompletedMatches,
  selectTournamentIdFromMatchId,
} from "selectors/Match";
import { useAppSelector } from "app/hooks";
import { shallowEqual } from "react-redux";

interface Props {
  trackId: string;
  matchIds: string[];
}

// todo - doesn't support specific formats anymore, also kinda gross code xd
export const TrackTeamStats = ({ trackId, matchIds }: Props) => {
  const completeMatches = useAppSelector(
    (state) => selectCompletedMatches(state, matchIds),
    shallowEqual
  );

  const scopedRosterIds = getMatchesScopedRosterIds(completeMatches);
  if (scopedRosterIds.every((r) => r.scope === "Driver")) {
    return <></>;
  }

  return (
    <TrackTeamStatTable
      columns={sharedTrackTeamStatTableColumns}
      scopedRosterIds={scopedRosterIds}
      matches={completeMatches}
      trackId={trackId}
      statGetter={getSharedTeamTrackStats}
      dontShowWhen={(t) =>
        t.bestTime === undefined &&
        t.banRate === undefined &&
        t.pickRate === undefined
      }
    />
  );
};

interface TrackTeamStatTableProps<T> {
  columns: StatTableColumn<T>[];
  scopedRosterIds: ScopedRosterId[];
  matches: Match[];
  trackId: string;
  statGetter: (
    trackId: string,
    rosterId: string,
    orgId: string | undefined,
    displayName: string,
    teamMatches: Match[]
  ) => T;
  dontShowWhen: (teamStats: T) => boolean;
}

function TrackTeamStatTable<T extends SharedTeamTrackStats>({
  columns,
  scopedRosterIds,
  matches,
  trackId,
  statGetter,
  dontShowWhen,
}: TrackTeamStatTableProps<T>) {
  const stats = nonUndefined(
    useAppSelector(
      (state) =>
        scopedRosterIds.map((scopedRoster) => {
          const rosterMatches = matches.filter((m) =>
            matchContainsRoster(m, scopedRoster.id, scopedRoster.scope)
          );

          // todo - bad
          const matchId = rosterMatches.at(0)?.id;
          const tournamentId = !!matchId
            ? selectTournamentIdFromMatchId(state, matchId)
            : undefined;

          if (!tournamentId) {
            return undefined;
          }

          const orgId = !!tournamentId
            ? selectRosterOrgId(state, scopedRoster.id, tournamentId)
            : undefined;
          const displayName = !!tournamentId
            ? selectRosterDisplayName(state, scopedRoster.id, tournamentId)
            : undefined;

          if (!displayName) {
            return undefined;
          }

          const stats = statGetter(
            trackId,
            scopedRoster.id,
            orgId,
            displayName,
            rosterMatches
          );

          if (dontShowWhen(stats)) {
            return undefined;
          }

          return stats;
        }),
      arrayCompareWithShallowEquals
    )
  );

  if (stats.length === 0) {
    return <></>;
  }

  return (
    <StatTable
      stats={stats}
      keyGetter={(team) => team.teamId}
      columns={columns}
      initialSortColumnIdx={0}
      paginationAmount={20}
      searchFunction={(arg, searchText) =>
        anyStringContains(searchText, arg.displayName)
      }
      searchPlaceholder="Filter: Team Name. Comma for multiple"
    />
  );
}
